import { useCallback } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';

import { IRestaurant } from '@rbi-ctg/store';
import { ServiceMode } from 'generated/graphql-gateway';
import { getStaticPathname } from 'navigation/location/location';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useOrderContext } from 'state/order';
import { ISelectOrderStore } from 'state/order/types';
import { useServiceModeContext } from 'state/service-mode';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { routes } from 'utils/routing';

import { useDefaultServiceMode } from './use-default-service-mode';

export function useSelectStore() {
  const {
    selectOrderStore,
    reorder: { pendingReorder, setShouldHandleReorder },
  } = useOrderContext();
  const { redirectUrl } = useLocalSearchParams<{ redirectUrl: keyof typeof routes }>();

  const { selectServiceMode } = useServiceModeContext();
  const router = useRouter();

  const getDefaultServiceMode = useDefaultServiceMode();

  const dismissStoreLocator = useCallback(() => {
    const isStoreInfoModal = getStaticPathname().startsWith(routes.store);
    if (isStoreInfoModal) {
      router.dismiss();
      router.back();
    } else if (redirectUrl) {
      router.dismissTo(redirectUrl);
    } else {
      // Avoid dismissing arbitrary modals when selection is triggered outside the
      // store locator flow (e.g., deep links/autoselect). Let the caller control navigation.
      return;
    }
  }, [redirectUrl, router]);

  const selectStoreCallback = useCallback(
    async ({ newStore, serviceMode }: { newStore: IRestaurant; serviceMode: ServiceMode }) => {
      dismissStoreLocator();
      // TODO: We probably want to use dynamic event name here
      logEvent(CustomEventNames.ORDER_HERE_STORE_CONFIRMATION_FLOW, EventTypes.Navigation, {
        'Restaurant Id': newStore._id,
        'Restaurant Name': newStore.name,
      });
      // set service mode to ensure we aren't taken back to static menu
      // need to do this after previous checks so that we don't set service mode
      // when enableOrdering is false
      selectServiceMode(serviceMode);

      // if user has a pending reorder return and let the useEffect handle it below
      if (pendingReorder) {
        setShouldHandleReorder(true);
        return;
      }

      LocalStorage.setItem(StorageKeys.SKIP_AUTO_SELECT_PROMPT, true);
    },
    [selectServiceMode, pendingReorder, dismissStoreLocator, setShouldHandleReorder]
  );

  return useCallback(
    async ({
      newStore,
      requestedServiceMode,
      skipChangeStoreMessage,
      callback,
    }: ISelectOrderStore) => {
      const defaultServiceMode = getDefaultServiceMode(newStore);
      const finalServiceMode = requestedServiceMode ?? defaultServiceMode;

      await selectOrderStore({
        newStore,
        requestedServiceMode: finalServiceMode,
        skipChangeStoreMessage,
        callback: () => {
          selectStoreCallback({
            newStore,
            serviceMode: finalServiceMode,
          });
          callback?.();
        },
      });
    },
    [getDefaultServiceMode, selectOrderStore, selectStoreCallback]
  );
}
